// Media Attachment Web Component
// Displays OAuth-protected media files with appropriate viewers and reconnection support
import { escapeHtml } from '../utils/html-utils.js';
import { renderMissingMole } from '../utils/mascots.js';

class MediaAttachment extends HTMLElement {
  constructor() {
    super();
    this.fileUrl = null;
    this.provider = null;
    this.accountId = null;
    this.fileId = null;
    this.contentType = null;
    this.fileName = null;
    this.isLoading = false;
    this.hasError = false;
    this.errorMessage = null;
    this.requiresReconnect = false;
    this.proxyUrl = null;
  }

  static get observedAttributes() {
    return ['file-url', 'provider', 'account-id', 'file-id', 'content-type', 'file-name'];
  }

  connectedCallback() {
    this.parseAttributes();
    this.render();
    this.loadMedia();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.parseAttributes();
      this.render();
      if (name === 'file-url' || name === 'file-id') {
        this.loadMedia();
      }
    }
  }

  parseAttributes() {
    this.fileUrl = this.getAttribute('file-url') || '';
    this.provider = this.getAttribute('provider') || '';
    this.accountId = this.getAttribute('account-id') || '';
    this.fileId = this.getAttribute('file-id') || '';
    this.contentType = this.getAttribute('content-type') || '';
    this.fileName = this.getAttribute('file-name') || '';

    // Normalize contentType if it's just a file extension (e.g., "JPG" -> "image/jpeg")
    this.contentType = MediaAttachment.normalizeContentType(this.contentType);

    // If fileUrl is an OAuth URL, parse it
    if (this.fileUrl.startsWith('oauth://')) {
      const parts = this.fileUrl.substring('oauth://'.length).split('/');
      if (parts.length >= 3) {
        this.provider = this.provider || parts[0];
        this.accountId = this.accountId || parts[1];
        this.fileId = this.fileId || parts.slice(2).join('/');
      }
    }
  }

  static normalizeContentType(contentType) {
    if (!contentType) return '';

    // If it already looks like a MIME type (contains a slash), return as-is
    if (contentType.includes('/')) {
      return contentType;
    }

    // Otherwise, treat it as a file extension and convert to MIME type
    const ext = contentType.toLowerCase();
    const extensionToMimeType = {
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      // Video
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'ogv': 'video/ogg',
      'mov': 'video/quicktime',
      // Audio
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'flac': 'audio/flac',
      // Documents
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'txt': 'text/plain',
      'md': 'text/markdown'
    };

    return extensionToMimeType[ext] || contentType;
  }

  render() {
    this.innerHTML = `
      <div class="media-attachment" data-content-type="${this.contentType || 'unknown'}">
        ${this.renderContent()}
      </div>
    `;
    this.attachEventListeners();
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="media-loading">
          <div class="spinner"></div>
          <p>Loading media...</p>
        </div>
      `;
    }

    if (this.hasError) {
      return this.renderError();
    }

    if (!this.proxyUrl) {
      return `
        <div class="media-placeholder">
          <p>Media not available</p>
        </div>
      `;
    }

    return this.renderMedia();
  }

  renderError() {
    return `
      <div class="media-error">
        <div class="error-icon">⚠️</div>
        <div class="error-content">
          <p class="error-message">${this.errorMessage || 'Failed to load media'}</p>
          ${this.requiresReconnect ? `
            <button class="btn btn-primary reconnect-btn">
              🔗 Reconnect Account
            </button>
            <p class="error-hint">Your connection to ${this.provider} has expired. Please reconnect to view this media.</p>
          ` : `
            <button class="btn btn-secondary retry-btn">
              🔄 Retry
            </button>
          `}
        </div>
      </div>
    `;
  }

  renderMedia() {
    const mimeType = this.contentType || this.detectContentType();

    if (mimeType.startsWith('image/')) {
      return this.renderImage();
    } else if (mimeType.startsWith('video/')) {
      return this.renderVideo();
    } else if (mimeType.startsWith('audio/')) {
      return this.renderAudio();
    } else if (mimeType.includes('pdf')) {
      return this.renderPDF();
    } else if (mimeType.includes('text/') || mimeType.includes('document')) {
      return this.renderDocument();
    } else {
      return this.renderGeneric();
    }
  }

  renderImage() {
    return `
      <div class="media-image-container">
        <img 
          src="${escapeHtml(this.proxyUrl)}" 
          alt="${escapeHtml(this.fileName || 'Image')}"
          class="media-image"
          loading="lazy"
          onerror="this.closest('media-attachment').handleImageError()"
        />
        ${this.fileName ? `<div class="media-caption">${escapeHtml(this.fileName)}</div>` : ''}
      </div>
    `;
  }

  renderVideo() {
    return `
      <div class="media-video-container">
        <video 
          src="${escapeHtml(this.proxyUrl)}"
          controls
          class="media-video"
          preload="metadata"
        >
          Your browser does not support the video tag.
        </video>
        ${this.fileName ? `<div class="media-caption">${escapeHtml(this.fileName)}</div>` : ''}
      </div>
    `;
  }

  renderAudio() {
    return `
      <div class="media-audio-container">
        <div class="media-audio-player">
          <div class="audio-icon">🎵</div>
          <audio 
            src="${escapeHtml(this.proxyUrl)}"
            controls
            class="media-audio"
            preload="metadata"
          >
            Your browser does not support the audio tag.
          </audio>
        </div>
        ${this.fileName ? `<div class="media-caption">${escapeHtml(this.fileName)}</div>` : ''}
      </div>
    `;
  }

  renderPDF() {
    return `
      <div class="media-pdf-container">
        <div class="pdf-viewer">
          <iframe 
            src="${escapeHtml(this.proxyUrl)}"
            class="media-pdf"
            title="${escapeHtml(this.fileName || 'PDF Document')}"
          ></iframe>
        </div>
        <div class="media-actions">
          <a 
            href="${escapeHtml(this.proxyUrl)}" 
            target="_blank" 
            rel="noopener noreferrer"
            class="btn btn-secondary"
          >
            📄 Open in New Tab
          </a>
          ${this.fileName ? `<div class="media-caption">${escapeHtml(this.fileName)}</div>` : ''}
        </div>
      </div>
    `;
  }

  renderDocument() {
    return `
      <div class="media-document-container">
        <div class="document-preview">
          <div class="document-icon">📄</div>
          <div class="document-info">
            <div class="document-name">${escapeHtml(this.fileName || 'Document')}</div>
            <div class="document-type">${this.contentType || 'Document'}</div>
          </div>
        </div>
        <div class="media-actions">
          <a 
            href="${escapeHtml(this.proxyUrl)}" 
            target="_blank" 
            rel="noopener noreferrer"
            class="btn btn-primary"
          >
            📥 Download
          </a>
        </div>
      </div>
    `;
  }

  renderGeneric() {
    return `
      <div class="media-generic-container">
        <div class="generic-preview">
          <div class="generic-icon">${renderMissingMole()}</div>
          <div class="generic-info">
            <div class="generic-name">${escapeHtml(this.fileName || 'File')}</div>
            <div class="generic-type">${this.contentType || 'Unknown type'}</div>
          </div>
        </div>
        <div class="media-actions">
          <a 
            href="${escapeHtml(this.proxyUrl)}" 
            target="_blank" 
            rel="noopener noreferrer"
            class="btn btn-primary"
          >
            📥 Download
          </a>
        </div>
      </div>
    `;
  }

  detectContentType() {
    if (!this.fileName && !this.fileId) {
      return 'application/octet-stream';
    }

    const name = this.fileName || this.fileId;
    const ext = name.split('.').pop()?.toLowerCase();

    const mimeTypes = {
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      // Video
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'ogv': 'video/ogg',
      'mov': 'video/quicktime',
      // Audio
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'flac': 'audio/flac',
      // Documents
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'txt': 'text/plain',
      'md': 'text/markdown'
    };

    return mimeTypes[ext] || 'application/octet-stream';
  }

  async loadMedia() {
    if (!this.fileUrl && !this.fileId) {
      this.hasError = true;
      this.errorMessage = 'No file URL or ID provided';
      this.render();
      return;
    }

    // If it's already a proxy URL, use it directly
    if (this.fileUrl && !this.fileUrl.startsWith('oauth://')) {
      this.proxyUrl = this.fileUrl;
      this.render();
      return;
    }

    // For OAuth URLs, we need to get a proxy URL
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = null;
    this.requiresReconnect = false;
    this.render();

    try {
      // Get proxy URL from the API
      const response = await fetch('/api/images/proxy-urls', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          image_urls: [this.fileUrl || `oauth://${this.provider}/${this.accountId}/${this.fileId}`],
          post_id: null // Could be passed as attribute if needed
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.requiresReconnect) {
          this.requiresReconnect = true;
          this.errorMessage = 'Your connection has expired. Please reconnect your account.';
        } else {
          this.errorMessage = 'Failed to load media';
        }
        throw new Error(this.errorMessage);
      }

      const data = await response.json();
      const proxyUrlItem = data.proxy_urls?.[0];

      if (!proxyUrlItem || !proxyUrlItem.proxy_url) {
        throw new Error('No proxy URL returned');
      }

      this.proxyUrl = proxyUrlItem.proxy_url;
      this.isLoading = false;
      this.render();
    } catch (error) {
      window.logger.error('Error loading media:', error);
      this.isLoading = false;
      this.hasError = true;
      if (!this.errorMessage) {
        this.errorMessage = error.message || 'Failed to load media';
      }
      this.render();
    }
  }

  handleImageError() {
    this.hasError = true;
    this.errorMessage = 'Failed to load image';
    this.render();
  }

  attachEventListeners() {
    const reconnectBtn = this.querySelector('.reconnect-btn');
    reconnectBtn?.addEventListener('click', () => {
      window.location.href = '/settings/sparkle-sources';
    });

    const retryBtn = this.querySelector('.retry-btn');
    retryBtn?.addEventListener('click', () => {
      this.loadMedia();
    });
  }

}

customElements.define('media-attachment', MediaAttachment);

export default MediaAttachment;

